<template>
    <div class="card">

        <div class="bg-blue-light p-1">
            <TitleButton
                :showBtn="true"
                :showAddNew="false"
                title="POS Invoice"
                @onClickCloseButton="goToList"
            />
        </div>

        <div class="my-2 px-2">
            <TableHeaderElectronicInvoice
                :formData="formData"
                :tableItems="tableItems"
                :additionalProductQuery="additionalProductQuery"
                ref="formHeader"
            />
        </div>

        <div class="col-12 px-2">
            <InvoiceElectronicTable
                :tableItems="tableItems"
                @addNewItem="addNewItem"
                :salesAccountHeads="salesAccountHeads"
                :vatRate="vatRate"
            />
        </div>
        <div class="row px-2">
            <div class="col-md-6">
                <div class="mb-1">
                    <label class="form-label" for="description">Ledger Memo</label>
                    <vField
                        as="textarea"
                        name="description"
                        type="number"
                        class="form-control"
                        v-model="formData.description"
                    />
                </div>
            </div>
            <div class="col-md-5 ml-auto mt-auto mb-1">
                <div class="d-flex flex-wrap gap-1 gy-2">
                    <button :disabled="saveButtonLoader" @click="handleSubmit(false)" class="btn btn-primary">Save</button>
                    <button :disabled="saveButtonLoader" @click="handleSubmit(true)" class="btn btn-primary">Save & Print</button>
                </div>
            </div>
        </div>

    </div>
</template>

<script setup>
import {computed, inject, onMounted, provide, ref} from 'vue';
import useDate from "@/services/utils/day";
import {vatRate} from "@/data/inventory.js";
import {useRoute, useRouter} from 'vue-router';
import handleCompany from "@/services/modules/company";
import TitleButton from "@/components/atom/TitleButton";
import handlePurchase from "@/services/modules/purchase";
import InvoiceElectronicTable from "@/components/molecule/company/inventory/electronic-invoice/InvoiceElectronicTable.vue";
import TableHeaderElectronicInvoice from "@/components/molecule/company/inventory/electronic-invoice/TableHeaderElectronicInvoice.vue";
import pdfPrinterHelper from "@/services/utils/electronic-invoice/pdfPrinterHelper";
import pdfPrinter from '@/services/utils/pdfPrinter';
import {useStore} from "vuex";
import handleBarcode from "@/services/modules/barcode";
import figureFormatter from "@/services/utils/figureFormatter";

    const { generatePdf } = pdfPrinterHelper();
    const { fetchSalesBill, ...rest } = handlePurchase();
    const { fetchCompanyInfo } = handleCompany();
    const { exportToPDF } = pdfPrinter();
    const { fetchBarcode } = handleBarcode();
    const { formatDate } = figureFormatter();

    const $route = useRoute();
    const $router = useRouter();
    const date = useDate();
    const store = useStore()
    const saveButtonLoader = ref(false)
    const companyId = $route.params.companyId;
    const loader = ref(true);
    const formHeader = ref(null);
    const salesAccountHeads = ref([]);
    const totals = ref({});
    const invoiceRes = ref({});
    const company = ref({});
    const barcodeQuery = ref('');
    const barcode = ref('');
    const qrcode = ref('');
    let formData = ref({
        company_id: companyId,
        contact_profile_id: null,
        account_head_id: null,
        ipd_register_id: null,
        vat_payable_account_head_id: null,
        project_id: null,
        cost_centre_id: null,
        location_id: null,
        mop_account_head_id: null,
        discount_head_id: null,
        service_resource_id: null,
        receipt_reference: null,
        adjustment_amount: null,
        receipt_description: "Electronic bill payment",
        payment: null,
        status: 'active',
        date: '',
        has_item_detail: true,
        description: '',
        order_number: null,
        sales_person_id: null,
        commission_percent: null,
        lc_number: null,
        advance_receipt_id: null,
        advance_amount: 0,
        item_details: []
    });
    provide('formData', formData);
    provide('totals', totals);
    const showSuccess =  inject('showSuccess');
    const showError =  inject('showError');

    const additionalProductQuery = computed(() => {
        return {
            location_id: formData.value.location_id,
            groupByBatch: 1
        }
    })

    onMounted( async() => {
        loader.value=true
        const companyQuery = `?company_id=${companyId}`;
        Promise.all([
            rest.fetchAccountReceivable(companyQuery).then(res=> {
                if(res.data) formData.value.account_head_id = res.data[0].id;
            }),
            rest.fetchVatPayable(companyQuery).then(res=> {
                if(res.data) formData.value.vat_payable_account_head_id = res.data[0].id;
            }),
            fetchCompanyInfo(companyId).then(res=> {
                if(res.data) company.value = res.data;
            }),
            rest.getAccountHeadBySlag("discount_account", companyQuery).then(res => {
                if(res.data) {
                  salesAccountHeads.value = res.data
                }
            })
        ])
        .finally(() => {loader.value=false})
    })

    let tableItems = ref([]);

    const addNewItem = () => {

        tableItems.value.push({
            product_id: null,
            name: '',
            description: '',
            quantity : 1,
            rate: 0,
            discount_percent: 0,
            vat: 0,
            sub_total: 0,
            discount_amount: 0,
            vat_amount: 0,
            total: 0,
            isEdit: false,
        });

    }

    const goToList = () => {
        $router.push({name: 'invoice-electronics-list',
            params: {
                companyId: $route.params.companyId,
                moduleId: $route.params.moduleId,
                menuId: $route.params.menuId,
                pageId: $route.params.pageId
            },
            query: {
                start: $route.query.start,
                end: $route.query.end
            }

        })
    }

    const resetForm = () => {
        const accountHeadId = formData.value.account_head_id
        const vatPayableAccountHeadId = formData.value.vat_payable_account_head_id
        formData.value = {
            company_id: companyId,
            contact_profile_id: null,
            ipd_register_id: null,
            project_id: null,
            account_head_id: accountHeadId,
            cost_centre_id: null,
            location_id: null,
            mop_account_head_id: null,
            vat_payable_account_head_id: vatPayableAccountHeadId,
            discount_head_id: null,
            service_resource_id: null,
            receipt_reference: null,
            adjustment_amount: null,
            receipt_description: "Electronic bill payment",
            payment: null,
            vat_challan_account_head_id: null,
            vat_challan_amount: null,
            ait_challan_account_head_id: null,
            ait_challan_amount: null,
            discount_account_head_id: null,
            discount_amount: null,
            status: 'active',
            date: '',
            has_item_detail: true,
            description: '',
            order_number: null,
            sales_person_id: null,
            commission_percent: null,
            lc_number: null,
            item_details: []
        };
        formHeader.value.resetData()
        tableItems.value = [];
    }

    const handleSubmit = (print=false) => {
        if(!formData.value.date) formData.value.date = date.currentDate();
        formData.value.item_details = tableItems;
        let copyFormData = JSON.parse(JSON.stringify(formData.value))
        copyFormData.item_details = formData.value.item_details
        copyFormData.sale_type = "pos_invoice"
        copyFormData.receipt_type = "pos_invoice"
        copyFormData.sale_master_receipt = true
        saveButtonLoader.value = true
        rest.storeBillPayment(copyFormData)
        .then(res => {
            if(res.status) {
                showSuccess(res.message)
                if(print) goToPrint(res.data.id)
                resetForm();
                return;
            }
            showError(res.message)
        })
        .catch(err=> {
            console.log(err)
            showError(err.response.data.message)
        }).finally(() => {
           saveButtonLoader.value = false
        })
    }

    const goToPrint = (id) => {
       const user = store.state.user.user
        const companyQuery = `?company_id=${companyId}`;
        fetchSalesBill(id, companyQuery).then( (res) =>{
          if( res ){
            invoiceRes.value = res.data;
            let qrcodeData = JSON.stringify({
              ref_no: invoiceRes.value.bill_number,
              ref_date: invoiceRes.value.date,
              created_by: user.name,
              created_date_time: formatDate(new Date())
            })
            barcodeQuery.value = `?code=${invoiceRes.value.bill_number}&qrcode=${qrcodeData}`;
          }}).then (() => {
          fetchBarcode(barcodeQuery.value).then( (res) => {
            barcode.value = res.barcode;
            qrcode.value = res.qrcode
          }).then( () => {
            exportToPDF(company.value, invoiceRes.value, barcode.value, qrcode.value, user.name, 1)
          })
        }).catch( (e) => {
          console.log("Something went wrong");
        })
    }

</script>
<style scoped>
address{
    margin-bottom: 0 !important;
}
</style>
